import type {
  PhaseScorecard,
  RunScorecard,
  ScorecardAttribute,
  ScorecardCategory,
} from '../types/run-content.api.types';

import { formatScoreTrio, humanizeFieldPath, type ScoreTrio } from './run-format';

/**
 * One attribute's before → after coverage, mapped from content-generation's scorecard
 * (`RunScorecard.attributes`) into the legacy "enrichment coverage" shape (catalog-operations'
 * `AttributeCoverageRow`/`CoverageAttributeRow`) — the closest valid source for catalog-service
 * runs, since catalog-operations' own `workflow/v1/enrichments/{workflowId}/coverage` endpoint is
 * a different pipeline (integration-service) with no catalog-enrichment run id in its space.
 */
export interface AttributeCoverageRow {
  /** Unique per row — `name`, plus `#n` when the scorecard sent the same name more than once. */
  key: string;
  name: string;
  /** Set when the scorecard sent this `name` on several rows (e.g. two source fields both scored
   *  as `google_product_category`): which one this is, so neither is dropped or mislabelled. */
  duplicate: { index: number; total: number } | null;
  before: number | null;
  after: number | null;
  lift: number | null;
  isCore: boolean;
  /** `after < before` — coverage is expected to be monotone upstream, so a drop is a real flag. */
  isRegression: boolean;
}

/** Maps a scorecard's attribute roll-up to display rows, regressions first then by lift. Shared
 *  by the run-level `attributes[]` and each category's own nested `attributes[]`. */
export function attributeCoverageRows(
  attributes: readonly ScorecardAttribute[] | undefined,
): AttributeCoverageRow[] {
  if (!attributes) return [];
  // The scorecard groups by its raw source field but names each row by a mapped score name, so two
  // source keys can arrive under one `name` — keep both, numbered in the order they were sent.
  const totals = new Map<string, number>();
  for (const a of attributes) totals.set(a.name, (totals.get(a.name) ?? 0) + 1);
  const seen = new Map<string, number>();
  return attributes
    .map((a) => {
      const total = totals.get(a.name) ?? 1;
      const index = (seen.get(a.name) ?? 0) + 1;
      seen.set(a.name, index);
      return { a, duplicate: total > 1 ? { index, total } : null };
    })
    .map(({ a, duplicate }) => ({
      key: duplicate ? `${a.name}#${String(duplicate.index)}` : a.name,
      name: a.name,
      duplicate,
      before: a.before,
      after: a.after,
      lift: a.lift,
      isCore: !!a.isCore,
      isRegression:
        typeof a.before === 'number' && typeof a.after === 'number' && a.after < a.before,
    }))
    .sort((a, b) => {
      if (a.isRegression !== b.isRegression) return a.isRegression ? -1 : 1;
      return (b.lift ?? 0) - (a.lift ?? 0);
    });
}

/** One product category's before → after coverage, with its own attribute rows. */
export interface CategoryCoverageRow {
  category: string;
  before: number | null;
  after: number | null;
  lift: number | null;
  productsAfter: number;
  isRegression: boolean;
  attributes: AttributeCoverageRow[];
}

/**
 * Maps the scorecard's `categories[]` (catalog-service `CategoryScorecard` — additive, confirmed
 * present in `score_scorecard.py`'s `RunScorecard.categories`) to display rows, regressions first
 * then by lift. Absent on an older deploy that hasn't shipped it yet; the caller degrades to the
 * attribute-only view, never a fake "unavailable".
 */
export function categoryCoverageRows(
  categories: readonly ScorecardCategory[] | undefined,
): CategoryCoverageRow[] {
  if (!categories) return [];
  return categories
    .map((c) => ({
      category: c.category,
      before: c.before,
      after: c.after,
      lift: c.lift,
      productsAfter: c.productsAfter,
      isRegression:
        typeof c.before === 'number' && typeof c.after === 'number' && c.after < c.before,
      attributes: attributeCoverageRows(c.attributes),
    }))
    .sort((a, b) => {
      if (a.isRegression !== b.isRegression) return a.isRegression ? -1 : 1;
      return (b.lift ?? 0) - (a.lift ?? 0);
    });
}

/** Boilerplate tokens that prefix some cross-field criterion ids without naming anything — e.g.
 *  `cross_field_gtin_missing` reads as "Gtin missing", not "Cross field gtin missing". The
 *  scorecard sends no label for `topIssues[].criterionId` (catalog-service `IssueSummary` carries
 *  only the id, confirmed against `score_scorecard.py`), so this is the best available fallback. */
const CRITERION_FILLER_PREFIXES = [
  'cross_field_',
  'cross-field-',
  'criterion_',
  'issue_',
  'check_',
];

/** A cross-field issue's display label — strips a filler prefix, then humanizes the slug; a dotted
 *  id (`Description.readability`) reads "Description · Readability" via `humanizeFieldPath`. */
export function criterionLabel(criterionId: string): string {
  const lower = criterionId.toLowerCase();
  const prefix = CRITERION_FILLER_PREFIXES.find((p) => lower.startsWith(p));
  return humanizeFieldPath(prefix ? criterionId.slice(prefix.length) : criterionId);
}

/** A category slug as words — `health-and-beauty` → "Health & beauty". The scorecard sends only
 *  the slug (`ScorecardCategory.category`: lowercase, `&` → `and`), so this undoes that encoding. */
export function categoryLabel(slug: string): string {
  const words = slug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((w) => (w === 'and' ? '&' : w));
  const text = words.join(' ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : slug;
}

/** One coverage row with its rounded before/after/lift trio — the bar and the numbers share it. */
export interface CoverageRowView {
  row: AttributeCoverageRow;
  trio: ScoreTrio;
}

/** Attaches each row's rounded trio (from the row's own `before`/`after`). */
function withTrio(rows: readonly AttributeCoverageRow[]): CoverageRowView[] {
  return rows.map((row) => ({ row, trio: formatScoreTrio(row.before, row.after) }));
}

/** Attribute coverage sort keys — the Insights toolbar's "Sort" options, default `attention`. */
export type AttributeSort = 'attention' | 'gain' | 'drop' | 'afterDesc' | 'afterAsc' | 'name';
/** Attribute coverage filter keys — the Insights toolbar's "Show" options, default `all`. */
export type AttributeFilter = 'all' | 'improved' | 'dropped' | 'unchanged' | 'core';

/** Orders two nullable numbers in `dir` (1 = ascending, -1 = descending), missing values always
 *  last — a row with nothing to compare never outranks one that moved. */
function nullsLast(a: number | null, b: number | null, dir: 1 | -1): number {
  if (a === null || b === null) return a === null ? (b === null ? 0 : 1) : -1;
  return (a - b) * dir;
}

/** "Needs attention first" rank: drops, then gains, then values new in this batch, then the rest. */
function attentionRank(view: CoverageRowView): number {
  const { preValue, liftValue } = view.trio;
  if (view.row.isRegression || (liftValue !== null && liftValue < 0)) return 0;
  if (liftValue !== null && liftValue > 0) return 1;
  if (preValue === null && view.trio.postValue !== null) return 2;
  return 3;
}

/** True for a row that didn't move — folded into the "N unchanged" group under the list. */
export function isUnchangedView(view: CoverageRowView): boolean {
  return view.trio.preValue !== null && view.trio.liftValue === 0 && !view.row.isRegression;
}

/** Each attribute row paired with its rounded trio, in the input order. */
export function coverageRowViews(rows: readonly AttributeCoverageRow[]): CoverageRowView[] {
  return withTrio(rows);
}

/** True when an attribute row belongs in the chosen filter bucket (moved up / dropped / flat / core). */
export function matchesAttributeFilter(view: CoverageRowView, filter: AttributeFilter): boolean {
  const lift = view.trio.liftValue;
  switch (filter) {
    case 'improved':
      return lift !== null && lift > 0;
    case 'dropped':
      return view.row.isRegression;
    case 'unchanged':
      return lift === 0 && !view.row.isRegression;
    case 'core':
      return view.row.isCore;
    default:
      return true;
  }
}

/** Sorts attribute rows by the toolbar's key; `label` resolves the display name for A–Z so the
 *  order matches what the user reads, not the raw field key. */
export function sortAttributeViews(
  views: readonly CoverageRowView[],
  sort: AttributeSort,
  label: (row: AttributeCoverageRow) => string,
): CoverageRowView[] {
  const byName = (a: CoverageRowView, b: CoverageRowView) =>
    label(a.row).localeCompare(label(b.row));
  return [...views].sort((a, b) => {
    switch (sort) {
      case 'drop':
        return nullsLast(a.trio.liftValue, b.trio.liftValue, 1) || byName(a, b);
      case 'afterDesc':
        return nullsLast(a.trio.postValue, b.trio.postValue, -1) || byName(a, b);
      case 'afterAsc':
        return nullsLast(a.trio.postValue, b.trio.postValue, 1) || byName(a, b);
      case 'name':
        return byName(a, b);
      case 'attention': {
        const rank = attentionRank(a) - attentionRank(b);
        if (rank !== 0) return rank;
        // Within drops the biggest first; within gains the biggest first; the rest by name.
        if (attentionRank(a) === 0) return nullsLast(a.trio.liftValue, b.trio.liftValue, 1);
        if (attentionRank(a) === 1) {
          return nullsLast(a.trio.liftValue, b.trio.liftValue, -1) || byName(a, b);
        }
        return byName(a, b);
      }
      default:
        return nullsLast(a.trio.liftValue, b.trio.liftValue, -1) || byName(a, b);
    }
  });
}

/** Category sort keys — default `attention`; `products` orders by `productsAfter`. */
export type CategorySort = 'attention' | 'gain' | 'drop' | 'products' | 'name';
/** Category filter keys — default `all`. */
export type CategoryFilter = 'all' | 'drops' | 'improved';

/** True when the category itself dropped or any of its fields did — the card's amber flag. */
export function categoryHasDrops(cat: CategoryCoverageRow): boolean {
  return cat.isRegression || cat.attributes.some((r) => r.isRegression);
}

/** True when a category belongs in the chosen filter bucket. */
export function matchesCategoryFilter(cat: CategoryCoverageRow, filter: CategoryFilter): boolean {
  if (filter === 'drops') return categoryHasDrops(cat);
  if (filter === 'improved') {
    const lift = formatScoreTrio(cat.before, cat.after).liftValue;
    return lift !== null && lift > 0;
  }
  return true;
}

/** Sorts categories by the toolbar's key — lift uses the same rounded trio the card shows (R3-1). */
export function sortCategories(
  cats: readonly CategoryCoverageRow[],
  sort: CategorySort,
): CategoryCoverageRow[] {
  const lift = (c: CategoryCoverageRow) => formatScoreTrio(c.before, c.after).liftValue;
  const byName = (a: CategoryCoverageRow, b: CategoryCoverageRow) =>
    categoryLabel(a.category).localeCompare(categoryLabel(b.category));
  return [...cats].sort((a, b) => {
    switch (sort) {
      case 'drop':
        return nullsLast(lift(a), lift(b), 1) || byName(a, b);
      case 'products':
        return b.productsAfter - a.productsAfter || byName(a, b);
      case 'name':
        return byName(a, b);
      case 'attention':
        // Categories with a drop first, then the biggest gain.
        return (
          Number(categoryHasDrops(b)) - Number(categoryHasDrops(a)) ||
          nullsLast(lift(a), lift(b), -1) ||
          byName(a, b)
        );
      default:
        return nullsLast(lift(a), lift(b), -1) || byName(a, b);
    }
  });
}

/** One scorecard issue (`RunScorecard.topIssues[]`). */
export type ScorecardIssue = RunScorecard['topIssues'][number];
/** Issue sort keys — default `productsDesc`. */
export type IssueSort = 'productsDesc' | 'productsAsc' | 'name';

/** The field an issue belongs to — the criterion id's dotted prefix (`description.readability` →
 *  `description`); `null` for a flat cross-field id (`CF-01`), which names no single field. */
export function issueFieldKey(criterionId: string): string | null {
  const lower = criterionId.toLowerCase();
  const prefix = CRITERION_FILLER_PREFIXES.find((p) => lower.startsWith(p));
  const id = prefix ? criterionId.slice(prefix.length) : criterionId;
  const dot = id.indexOf('.');
  return dot > 0 ? id.slice(0, dot) : null;
}

/** An issue's reason as a short sentence-case phrase — the criterion id minus its field prefix
 *  (`description.no_superlatives` → "No superlatives"); a flat id humanises whole. */
export function criterionReason(criterionId: string): string {
  const lower = criterionId.toLowerCase();
  const prefix = CRITERION_FILLER_PREFIXES.find((p) => lower.startsWith(p));
  const id = prefix ? criterionId.slice(prefix.length) : criterionId;
  const dot = id.indexOf('.');
  const words = (dot > 0 ? id.slice(dot + 1) : id)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : criterionId;
}

/** How widespread an issue is, by its share of scored products: ≥50 % high, 20–49 % medium. */
export type IssueSeverity = 'high' | 'medium' | 'low';

/** Severity band for a 0–100 share of scored products. */
export function issueSeverity(sharePct: number): IssueSeverity {
  if (sharePct >= 50) return 'high';
  if (sharePct >= 20) return 'medium';
  return 'low';
}

/** Sorts issues by product count (missing counts last) or by display label. */
export function sortIssues(issues: readonly ScorecardIssue[], sort: IssueSort): ScorecardIssue[] {
  const byName = (a: ScorecardIssue, b: ScorecardIssue) =>
    criterionLabel(a.criterionId).localeCompare(criterionLabel(b.criterionId));
  return [...issues].sort((a, b) => {
    if (sort === 'name') return byName(a, b);
    return nullsLast(a.products, b.products, sort === 'productsAsc' ? 1 : -1) || byName(a, b);
  });
}

/** Case-insensitive "any of these texts contains the query" — the toolbar search's one rule. */
export function textMatches(query: string, ...texts: string[]): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return texts.some((text) => text.toLowerCase().includes(q));
}

/** A category card's rows: drops first (never hidden behind "+N more"), then by |Δ| desc. */
export function rowsByAbsLift(rows: readonly AttributeCoverageRow[]): CoverageRowView[] {
  return withTrio(rows).sort((a, b) => {
    if (a.row.isRegression !== b.row.isRegression) return a.row.isRegression ? -1 : 1;
    const size = (v: CoverageRowView) =>
      v.trio.liftValue === null ? -1 : Math.abs(v.trio.liftValue);
    return size(b) - size(a);
  });
}

/** Text tone for a lift value — green for an improvement, red for a regression, muted at/near
 *  zero or when there is nothing to compare. Never defaults to "success" on a negative number. */
export function liftToneClass(lift: number | null | undefined): string {
  if (typeof lift !== 'number' || lift === 0) return 'text-muted-foreground';
  return lift > 0 ? 'text-foreground' : 'text-error';
}

/** One score tier's product count (PASS / REVIEW / FAIL), zero-counts dropped. */
export interface TierCount {
  tier: string;
  count: number;
}

/** Non-zero tier counts off a PRE/POST phase roll-up, in a fixed PASS → REVIEW → FAIL order. */
export function tierCounts(phase: PhaseScorecard | null | undefined): TierCount[] {
  if (!phase) return [];
  const order = ['PASS', 'REVIEW', 'FAIL'];
  return Object.entries(phase.tiers)
    .filter(([, count]) => count > 0)
    .sort(([a], [b]) => order.indexOf(a) - order.indexOf(b))
    .map(([tier, count]) => ({ tier, count }));
}

/** True when the scorecard has nothing to show (no phase averages and no attribute rows). */
export function isContentResultsEmpty(scorecard: RunScorecard | null | undefined): boolean {
  if (!scorecard) return true;
  return (
    scorecard.pre?.average == null &&
    scorecard.post?.average == null &&
    scorecard.attributes.length === 0
  );
}

/** A roll-up row that carries a number: content generation lists every attribute (and category)
 *  up front with `before`/`after` null, so a row's presence alone says nothing was measured. */
function hasValue(row: { before: number | null; after: number | null }): boolean {
  return row.before !== null || row.after !== null;
}

/** True once content generation has scored coverage — ≥1 attribute or category row on the
 *  scorecard with a before or after value (an all-dash table is not coverage). Gates the batch's
 *  Insights tab and the page itself (one rule, one read). */
export function hasCoverage(scorecard: RunScorecard | null | undefined): boolean {
  if (!scorecard) return false;
  return (
    scorecard.attributes.some(hasValue) ||
    (scorecard.categories ?? []).some(
      (category) => hasValue(category) || category.attributes.some(hasValue),
    )
  );
}
